import { useState } from "react";
import { Link } from "react-router-dom";
import { Store, ShoppingBasket, Truck, Pill, Stethoscope, Car, Smartphone, Hammer, Sofa, ArrowLeftRight } from "lucide-react";
import { SPECIALTIES } from "@/lib/clinic";
import { useApp } from "@/context/AppContext";
import { errMsg } from "@/lib/api";
import AuthLayout from "@/pages/AuthLayout";
import { BigButton, Chip, Field, inputCls } from "@/components/ui-kit";

const TYPES = [{ k: "grocery", icon: ShoppingBasket }, { k: "wholesale", icon: Truck }, { k: "pharmacy", icon: Pill }, { k: "clinic", icon: Stethoscope }, { k: "autoparts", icon: Car }, { k: "phones", icon: Smartphone }, { k: "hardware", icon: Hammer }, { k: "furniture", icon: Sofa }, { k: "exchange", icon: ArrowLeftRight }];
const CURRENCIES = ["DZD", "MRU", "EUR"];

export default function Register() {
  const { register, t } = useApp();
  const [f, setF] = useState({ shop_name: "", owner_name: "", email: "", password: "", pin: "", shop_type: "grocery", clinic_scope: "full", currency: "DZD", serves: [] });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target ? e.target.value : e });

  const submit = async (e) => {
    e.preventDefault();
    if (!/^\d{4}$/.test(f.pin)) return setErr(t("pin_4_digits"));
    setBusy(true);
    setErr("");
    try { await register(f); } catch (ex) { setErr(errMsg(ex)); }
    setBusy(false);
  };

  return (
    <AuthLayout title={t("create_shop")} subtitle={t("register_sub")}>
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <Field label={t("shop_name")}><input required value={f.shop_name} onChange={set("shop_name")} className={inputCls} data-testid="register-shop-name-input" /></Field>
        <Field label={t("shop_type")}>
          <select value={f.shop_type} onChange={set("shop_type")} className={inputCls} data-testid="register-type-select">
            {TYPES.map(({ k }) => <option key={k} value={k}>{t(`type_${k}`)}</option>)}
          </select>
        </Field>
        {f.shop_type === "wholesale" && (
          <Field label={t("ws_register_serves")}>
            <div className="flex gap-2 flex-wrap">
              {TYPES.filter(({ k }) => k !== "wholesale").map(({ k, icon: Icon }) => (
                <Chip key={k} active={f.serves.includes(k)} onClick={() => setF({ ...f, serves: f.serves.includes(k) ? f.serves.filter((x) => x !== k) : [...f.serves, k] })} data-testid={`register-serves-${k}`}><Icon className="w-4 h-4" />{t(`type_${k}`)}</Chip>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-1">{t("ws_register_serves_hint")}</p>
          </Field>
        )}
        {f.shop_type === "clinic" && (
          <Field label={t("clinic_scope")}>
            <div className="flex gap-2 flex-wrap mb-2">
              <Chip active={f.clinic_scope === "full"} onClick={() => set("clinic_scope")("full")} data-testid="register-scope-full">{t("scope_full")}</Chip>
              <Chip active={f.clinic_scope !== "full"} onClick={() => set("clinic_scope")("gynecology")} data-testid="register-scope-specialty">{t("scope_specialty")}</Chip>
            </div>
            {f.clinic_scope !== "full" && <select value={f.clinic_scope} onChange={set("clinic_scope")} className={inputCls} data-testid="register-scope-select">{SPECIALTIES.map((s) => <option key={s} value={s}>{t(`sp_${s}`)}</option>)}</select>}
            <p className="text-xs text-[#555] mt-1">{t("scope_hint")}</p>
          </Field>
        )}
        <Field label={t("owner_name")}><input required value={f.owner_name} onChange={set("owner_name")} className={inputCls} data-testid="register-owner-name-input" /></Field>
        <Field label={t("email")}><input type="email" dir="ltr" required value={f.email} onChange={set("email")} className={inputCls} data-testid="register-email-input" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("password")}><input type="password" dir="ltr" required minLength={6} value={f.password} onChange={set("password")} className={inputCls} data-testid="register-password-input" /></Field>
          <Field label={t("owner_pin")}><input inputMode="numeric" dir="ltr" maxLength={4} required value={f.pin} onChange={(e) => set("pin")(e.target.value.replace(/\D/g, ""))} className={`${inputCls} tracking-[0.5em] text-center font-bold`} data-testid="register-pin-input" /></Field>
        </div>
        <Field label={t("currency")}>
          <div className="flex gap-2">{CURRENCIES.map((c) => <Chip key={c} active={f.currency === c} onClick={() => set("currency")(c)} data-testid={`register-currency-${c}`}>{c}</Chip>)}</div>
        </Field>
        {err && <p className="text-destructive font-bold" data-testid="register-error">{err}</p>}
        <BigButton type="submit" variant="gold" disabled={busy} className="w-full" data-testid="register-submit-button"><Store className="w-5 h-5" />{t("create_shop")}</BigButton>
      </form>
      <p className="mt-6 text-center">
        {t("have_account")} <Link to="/login" className="text-primary font-bold" data-testid="go-login-link">{t("login")}</Link>
      </p>
    </AuthLayout>
  );
}
